import { FormControl, FormLabel, List, ListItem, Typography } from '@mui/material'
import { createElement } from 'react'
import { ElementFactory, Serializer, QuestionTextModel } from 'survey-core'
import { ReactQuestionFactory, SurveyQuestionElementBase } from 'survey-react-ui'
import { SurveyTextField } from './components/SurveyTextField'
import { parseSurveyText } from './components/parseSurveyText'
import { styles } from './SurveyText.styles'

const CUSTOM_TYPE = 'surveytext'

export class SurveyText extends QuestionTextModel {
  constructor (name: string) {
    super(name)
    this.titleLocation = 'hidden'
    this.createItemValues('bulletedDescription')
  }

  getType (): string {
    return CUSTOM_TYPE
  }

  get defaultText (): string {
    return this.getPropertyValue('defaultText')
  }

  set defaultText (val: string) {
    this.setPropertyValue('defaultText', val)
  }

  get bulletedDescription (): Array<{ value: number, text: string }> {
    return this.getPropertyValue('bulletedDescription')
  }

  set bulletedDescription (val) {
    this.setPropertyValue('bulletedDescription', val)
  }

  get singleLine (): boolean {
    return this.getPropertyValue('singleLine') ?? false
  }

  set singleLine (val: boolean) {
    this.setPropertyValue('singleLine', val)
  }
}

export function registerTextQuestion (): void {
  ElementFactory.Instance.registerElement(
    CUSTOM_TYPE,
    (name) => {
      return new SurveyText(name)
    }
  )

  Serializer.addClass(
    CUSTOM_TYPE,
    [{
      name: 'defaultText',
      type: 'text',
      category: 'general'
    },
    {
      name: 'bulletedDescription',
      type: 'itemvalues',
      category: 'general'
    },
    {
      name: 'singleLine',
      type: 'boolean',
      category: 'general'
    }
    ],
    function () {
      return new SurveyText('')
    },
    'text'
  )

  ReactQuestionFactory.Instance.registerQuestion(CUSTOM_TYPE, (props) => {
    return createElement(SurveyQuestionText, props)
  })
}

export class SurveyQuestionText extends SurveyQuestionElementBase {
  get question (): SurveyText {
    return this.questionBase as SurveyText
  }

  get value (): any {
    return this.question.value
  }

  handleChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const newValue = e.target.value
    this.question.value = newValue
  }

  renderElement (): JSX.Element {
    return (
      <FormControl>
        <FormLabel>
          {this.question.fullTitle}
          {!this.question.isRequired && this.question.requiredIf !== '' && (
            <Typography component="span" sx={{ fontStyle: 'italic', ml: 1 }}>
              optional
            </Typography>
          )}
        </FormLabel>
        {this.question.description != null && this.question.description !== '' && (
          <Typography sx={styles.description}>{parseSurveyText(this.question.description)}</Typography>
        )}
        {(this.question.bulletedDescription != null && this.question.bulletedDescription.length > 0) && (
          <List sx={{ listStyleType: 'disc', pl: 4, mb: 2 }}>
            {this.question.bulletedDescription.map((item: { value: number, text: string }, index) => (
              <ListItem key={index} sx={{ display: 'list-item', p: 0 }}>
                <Typography>{parseSurveyText(item.text)}</Typography>
              </ListItem>
            ))}
          </List>
        )}
        <SurveyTextField
          value={this.question.value ?? ''}
          placeholder={this.question.defaultText}
          onChange={this.handleChange}
          disabled={this.question.isReadOnly}
          multiline={!this.question.singleLine}
          height="110px"
          dataTestId={this.question.name}
          sx={{ width: this.question.singleLine ? '480px' : '750px', maxWidth: '100%' }}
        />
      </FormControl>
    )
  }
}
